const desktop = matchMedia('(min-width:990px)');
const reduceMotion = matchMedia('(prefers-reduced-motion:reduce)');
const revealedOffers = new Set();
const controllers = new Map();
function offerMoney(cents, format, fallback) {
  const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1];
  if (!token) return fallback;
  const whole = token.includes('no_decimals');
  const comma = token.includes('comma_separator');
  const space = token.includes('space_separator');
  const apostrophe = token.includes('apostrophe_separator');
  const digits = (cents / 100).toFixed(whole ? 0 : 2).split('.');
  const group = space ? '\u202f' : apostrophe ? "'" : comma ? '.' : ',';
  const number = digits[0].replace(/\B(?=(\d{3})+(?!\d))/g, group) + (whole ? '' : (comma || token === 'amount_with_space_separator' ? ',' : '.') + digits[1]);
  return format.replace(/\{\{\s*\w+\s*\}\}/, number);
}

function watchOffers(grid, details) {
  const running = new Map();
  const visible = new Set();
  const format = (grid.dataset.moneyFormat || '').replace(/<[^>]*>/g, '');
  const finish = card => {
    const frame = running.get(card);
    if (frame) cancelAnimationFrame(frame);
    running.delete(card);
    card.classList.remove('is-offer-revealing');
    card.querySelector('[data-rnf-price]').textContent = card.dataset.offerFinal;
    const percent = card.querySelector('[data-rnf-offer-percent]');
    const value = Number(card.dataset.offerFinalPercent) || 0;
    percent.textContent = `−${value}%`;
    percent.hidden = value === 0;
    card.querySelector('[data-rnf-offer]').hidden = value === 0;
    card.querySelector('[data-rnf-offer-auto]').hidden = card.dataset.offerAuto !== 'true';
  };
  const reveal = card => {
    if (!details.open || running.has(card)) return;
    if (card.dataset.offerAuto !== 'true' || revealedOffers.has(card.dataset.offerKey) || reduceMotion.matches) {
      finish(card);
      return;
    }
    revealedOffers.add(card.dataset.offerKey);
    const from = Number(card.dataset.offerFrom);
    const to = Number(card.dataset.offerTo);
    const base = Number(card.dataset.offerPercent);
    const final = Number(card.dataset.offerFinalPercent);
    const price = card.querySelector('[data-rnf-price]');
    const percent = card.querySelector('[data-rnf-offer-percent]');
    card.querySelector('[data-rnf-offer]').hidden = false;
    card.querySelector('[data-rnf-offer-auto]').hidden = false;
    percent.hidden = false;
    price.textContent = card.dataset.offerBase;
    percent.textContent = `−${base}%`;
    card.classList.add('is-offer-revealing');
    let start;
    let previous = from;
    const tick = time => {
      if (!details.open || reduceMotion.matches || document.hidden) { finish(card); return; }
      start ??= time;
      const progress = Math.max(0, Math.min(1, (time - start - 280) / 1500));
      const eased = 1 - (1 - progress) ** 3;
      const value = Math.round(from + (to - from) * eased);
      if (value !== previous) {
        price.textContent = offerMoney(value, format, card.dataset.offerFinal);
        previous = value;
      }
      percent.textContent = `−${Math.round(base + (final - base) * eased)}%`;
      if (progress < 1) running.set(card, requestAnimationFrame(tick));
      else finish(card);
    };
    running.set(card, requestAnimationFrame(tick));
  };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio >= .55) { visible.add(entry.target); reveal(entry.target); }
      else { visible.delete(entry.target); if (running.has(entry.target)) finish(entry.target); }
    });
  }, { threshold: [0, .55] });
  const finishAll = () => [...running.keys()].forEach(finish);
  const onVisibility = () => { if (document.hidden) finishAll(); };
  document.addEventListener('visibilitychange', onVisibility);
  return {
    observe: () => grid.querySelectorAll('.rnf-nav-card[data-offer-key]').forEach(card => observer.observe(card)),
    start: () => visible.forEach(reveal),
    finish: finishAll,
    update: (card, data) => {
      if (!data.offerKey || !card.querySelector('[data-rnf-offer]')) {
        card.querySelector('[data-rnf-price]').textContent = data.price;
        return;
      }
      finish(card);
      ['offerKey', 'offerFrom', 'offerTo', 'offerBase', 'offerFinal', 'offerPercent', 'offerFinalPercent', 'offerAuto'].forEach(key => { card.dataset[key] = data[key]; });
      finish(card);
      if (visible.has(card)) reveal(card);
    },
    dispose: () => { finishAll(); observer.disconnect(); document.removeEventListener('visibilitychange', onVisibility); }
  };
}

function watchImagePreviews(grid, details, options = {}) {
  const mediaSelector = options.mediaSelector || '.rnf-nav-card__media';
  const cardSelector = options.cardSelector || '.rnf-nav-card';
  const baseSelector = options.baseSelector || ':scope > img';
  const insidePhoto = (event, rect) => event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
  const finePointer = matchMedia('(any-hover:hover) and (any-pointer:fine)');
  const states = new Map();
  let active;
  const imageKey = image => new URL(image.getAttribute('src'), location.href).pathname;
  const decode = async image => {
    try {
      if (image.decode) await image.decode();
      else if (!image.complete) await new Promise((resolve, reject) => {
        const done = () => { cleanup(); resolve(); };
        const failed = () => { cleanup(); reject(new Error('Preview unavailable')); };
        const cleanup = () => { image.removeEventListener('load', done); image.removeEventListener('error', failed); };
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', failed, { once: true });
      });
      return image.naturalWidth > 0;
    } catch { return false; }
  };
  const show = (state, index) => {
    if (index === state.index) return;
    const distance = Math.abs(index - state.index);
    state.track.style.transitionDuration = `${Math.min(880, 500 + distance * 100)}ms`;
    state.track.style.transform = `translate3d(${-100 * index}%,0,0)`;
    state.index = index;
    state.media.dataset.rnfPreviewIndex = String(index);
    [...state.dots.children].forEach((dot, i) => dot.classList.toggle('is-current', i === index - 1));
  };
  const release = state => {
    if (state.disposed) return;
    clearTimeout(state.releaseTimer);
    state.disposed = true;
    state.track.replaceWith(state.base);
    state.dots.remove();
    delete state.media.dataset.rnfPreviewIndex;
    states.delete(state.media);
    state.media.dispatchEvent(new CustomEvent('rnf:preview:end', { bubbles: true, detail: { base: state.base } }));
  };
  const stop = () => {
    if (document.hidden || !details.open) {
      active = null;
      [...states.values()].forEach(state => {
        state.revision++;
        state.media.classList.remove('is-previewing');
        release(state);
      });
      return;
    }
    if (!active) return;
    active.revision++;
    active.media.classList.remove('is-previewing');
    show(active, 0);
    const previous = active;
    active = null;
    if (document.hidden || !details.open) release(previous);
    else previous.releaseTimer = setTimeout(() => release(previous),
      (parseFloat(previous.track.style.transitionDuration) || 600) + 60);
  };
  const build = media => {
    if (states.has(media)) {
      const existing = states.get(media);
      clearTimeout(existing.releaseTimer);
      media.dispatchEvent(new CustomEvent('rnf:preview:start', { bubbles: true, detail: { base: existing.base } }));
      return existing;
    }
    const pending = media.querySelector('[data-rnf-card-image]');
    if (pending) pending.replaceWith(pending.content.cloneNode(true));
    const base = media.querySelector(baseSelector);
    const template = media.querySelector('[data-rnf-hover-images]') || media.closest(cardSelector)?.querySelector('[data-rnf-hover-images]');
    if (!base || !template) return null;
    const seen = new Set([imageKey(base)]);
    const images = [...template.content.querySelectorAll('img')].filter(image => {
      const key = imageKey(image);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 3).map(image => image.cloneNode(true));
    if (!images.length) return null;
    const track = document.createElement('span');
    track.className = 'rnf-nav-preview-track';
    const dots = document.createElement('span');
    dots.className = 'rnf-nav-preview-dots';
    dots.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < images.length; i++) {
      const dot = document.createElement('i');
      if (!i) dot.className = 'is-current';
      dots.append(dot);
    }
    images.forEach(image => {
      image.loading = 'eager';
      image.fetchPriority = 'low';
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.style.backgroundImage = `url(${JSON.stringify(base.currentSrc || base.src)})`;
      image.style.backgroundSize = 'contain';
      image.style.backgroundPosition = 'center';
      image.style.backgroundRepeat = 'no-repeat';
    });
    media.insertBefore(track, base);
    track.append(base, ...images);
    media.append(dots);
    media.dataset.rnfPreviewIndex = '0';
    const state = { media, base, track, dots, count: images.length + 1, index: 0, desired: 0, revision: 0, disposed: false };
    state.ready = [Promise.resolve(true), ...images.map(async image => {
      let ready = await decode(image);
      if (state.disposed) return false;
      if (!ready) {
        image.removeAttribute('srcset');
        image.src = base.getAttribute('src');
        ready = await decode(image);
      }
      if (ready) ['background-image', 'background-size', 'background-position', 'background-repeat'].forEach(property => image.style.removeProperty(property));
      return ready;
    })];
    states.set(media, state);
    media.dispatchEvent(new CustomEvent('rnf:preview:start', { bubbles: true, detail: { base } }));
    return state;
  };
  const move = (state, event) => {
    const ratio = Math.max(0, Math.min(.999999, (event.clientX - state.rect.left) / state.rect.width));
    const index = 1 + Math.floor(ratio * (state.count - 1));
    if (state.desired === index) return;
    state.desired = index;
    const revision = ++state.revision;
    state.ready[index].then(ready => {
      if (!ready || state.disposed || active !== state || revision !== state.revision || !details.open || document.hidden || reduceMotion.matches || !desktop.matches) return;
      show(state, index);
    });
  };
  const onPointer = event => {
    if (event.pointerType !== 'mouse' || !finePointer.matches || !desktop.matches || reduceMotion.matches || !details.open) return;
    let media = event.target.closest(mediaSelector);
    if (!media && options.hitTestCard) {
      const card = event.target.closest(cardSelector);
      const candidate = card?.querySelector(mediaSelector);
      if (candidate) {
        const rect = active?.media === candidate ? active.rect : candidate.getBoundingClientRect();
        if (insidePhoto(event, rect)) media = candidate;
        else if (active?.media === candidate) stop();
      }
    }
    if (!media || !grid.contains(media)) return;
    if (active?.media !== media) {
      stop();
      active = build(media);
      if (!active) return;
      active.rect = media.getBoundingClientRect();
      active.desired = -1;
      media.classList.add('is-previewing');
    }
    move(active, event);
  };
  const onOut = event => {
    if (!active || active.media.contains(event.relatedTarget)) return;
    if (options.hitTestCard && event.relatedTarget && active.media.closest(cardSelector)?.contains(event.relatedTarget) && insidePhoto(event, active.rect)) return;
    stop();
  };
  const onVisibility = () => { if (document.hidden) stop(); };
  const reset = card => {
    const media = card.querySelector(mediaSelector);
    const state = states.get(media);
    if (!state) return;
    if (active === state) stop();
    release(state);
  };
  grid.addEventListener('pointerover', onPointer, { passive: true });
  grid.addEventListener('pointermove', onPointer, { passive: true });
  grid.addEventListener('pointerout', onOut, { passive: true });
  grid.addEventListener('pointercancel', stop, { passive: true });
  grid.addEventListener('scroll', stop, { passive: true });
  window.addEventListener('resize', stop, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  reduceMotion.addEventListener('change', stop);
  finePointer.addEventListener('change', stop);
  return { stop, reset, dispose: () => {
    stop();
    [...states.keys()].forEach(media => reset(media.closest(cardSelector)));
    grid.removeEventListener('pointerover', onPointer);
    grid.removeEventListener('pointermove', onPointer);
    grid.removeEventListener('pointerout', onOut);
    grid.removeEventListener('pointercancel', stop);
    grid.removeEventListener('scroll', stop);
    window.removeEventListener('resize', stop);
    document.removeEventListener('visibilitychange', onVisibility);
    reduceMotion.removeEventListener('change', stop);
    finePointer.removeEventListener('change', stop);
  } };
}


function mountGrid(grid) {
  if (controllers.has(grid)) return;
  const visible = {get open() { return grid.isConnected && !grid.closest('[hidden]'); }};
  const offers = watchOffers(grid, visible);
  const previews = watchImagePreviews(grid, visible);
  const align = () => grid.querySelectorAll('.rnf-nav-card__actions').forEach(actions => {
    const swatches = actions.querySelector('.rnf-nav-swatches');
    const button = actions.querySelector('.rnf-qb__trigger');
    if (!swatches || !button) return;
    const width = actions.clientWidth - 24;
    actions.classList.toggle('has-many-colors', swatches.scrollWidth > width - 2 * (button.offsetWidth + 8));
  });
  const resize = new ResizeObserver(align);
  resize.observe(grid);
  const onClick = event => {
    const swatch = event.target.closest('[data-rnf-variant]');
    if (!swatch || !grid.contains(swatch)) return;
    const card = swatch.closest('.rnf-nav-card');
    previews.reset(card);
    card.querySelectorAll('[data-rnf-variant]').forEach(item => item.setAttribute('aria-pressed', String(item === swatch)));
    card.querySelector('[data-rnf-product-link]').href = swatch.dataset.url;
    card.querySelector('[data-rnf-quick-buy]').dataset.rnfQuickBuy = swatch.dataset.url;
    const compare = card.querySelector('[data-rnf-compare]');
    compare.textContent = swatch.dataset.compare;
    compare.hidden = !swatch.dataset.compare;
    offers.update(card, swatch.dataset);
    const image = card.querySelector('[data-rnf-media-primary]');
    if (image && swatch.dataset.image) {
      window.__rnfCarouselMedia?.unmount(grid);
      const url = new URL(swatch.dataset.image, location.href);
      image.dataset.rnfQualitySrc = url.href;
      url.searchParams.set('format', 'jpg');
      image.dataset.rnfPosterSrc = url.href;
      image.src = url.href;
      image.toggleAttribute('data-rnf-animated-media', /\.gif$/i.test(url.pathname));
      delete image.dataset.rnfLoadState;
      window.__rnfCarouselMedia?.init(grid);
    }
  };
  grid.addEventListener('click', onClick);
  offers.observe();
  window.__rnfCarouselMedia?.init(grid);
  align();
  controllers.set(grid, {dispose() {
    previews.dispose(); offers.dispose(); resize.disconnect();
    grid.removeEventListener('click', onClick);
    window.__rnfCarouselMedia?.unmount(grid);
    controllers.delete(grid);
  }});
}
function mount(scope = document) {
  controllers.forEach((controller, grid) => { if (!grid.isConnected) controller.dispose(); });
  if (scope.matches?.('[data-rnf-collection-cards]')) mountGrid(scope);
  scope.querySelectorAll('[data-rnf-collection-cards]').forEach(mountGrid);
}
document.addEventListener('rnf:cards:mount', event => mount(event.detail?.root || event.target));
document.addEventListener('rnf:cards:unmount', event => {
  const scope = event.detail?.root || event.target;
  controllers.forEach((controller, grid) => { if (scope === grid || scope.contains(grid)) controller.dispose(); });
});
document.addEventListener('shopify:section:load', event => mount(event.target));
document.addEventListener('shopify:section:unload', event => {
  controllers.forEach((controller, grid) => { if (event.target.contains(grid)) controller.dispose(); });
});
const productGrid = document.getElementById('ProductGridContainer');
if (productGrid) new MutationObserver(() => mount(productGrid)).observe(productGrid, {childList: true});
desktop.addEventListener('change', () => controllers.forEach(controller => controller.previews?.stop()));
mount();
